import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { errorMessage, request } from '../api';

interface AdminRow {
  id: string;
  email: string;
  username: string;
  clientName: string;
  clientSlug: string;
  isOperatorTenant: boolean;
  status: string;
  lastLoginAt: string | null;
  createdAt: string;
}

/**
 * Who holds instance-wide access.
 *
 * Adding an administrator requires being one, so this page cannot bootstrap an
 * empty instance — the first one comes from `pnpm admin:create`, run against
 * the database. Both paths share the same rules on the server.
 */
export function Administrators(): JSX.Element {
  const admins = useQuery({
    queryKey: ['admin', 'admins'],
    queryFn: () => request<AdminRow[]>('GET', '/admin/admins'),
  });

  return (
    <>
      <header className="page-head">
        <h1>Administrators</h1>
        <p className="muted small" style={{ margin: 0 }}>
          Accounts that can see every tenant on this instance — this console, the client list, and
          the logs. Keep the number small.
        </p>
      </header>

      <div className="stack">
        <CreateAdmin />

        <section className="card">
          <strong>Administrators</strong>
          {admins.isLoading && <p className="muted small">Loading…</p>}
          {admins.isError && <p className="error small">{errorMessage(admins.error)}</p>}
          {admins.data && (
            <table style={{ marginTop: 10 }}>
              <thead>
                <tr>
                  <th>Account</th>
                  <th>Tenant</th>
                  <th>Status</th>
                  <th>Last sign-in</th>
                </tr>
              </thead>
              <tbody>
                {admins.data.map((admin) => (
                  <tr key={admin.id}>
                    <td>
                      {admin.email}
                      <div className="muted small">{admin.username}</div>
                    </td>
                    <td className="muted small">
                      {admin.clientName}
                      {/*
                        An administrator sitting inside a customer's tenant is
                        the pre-bootstrap arrangement: suspending or deleting
                        that client would take the operator's own access with
                        it. Worth flagging rather than hiding.
                      */}
                      {!admin.isOperatorTenant && (
                        <div className="warn small">inside a customer tenant</div>
                      )}
                    </td>
                    <td className={admin.status === 'ACTIVE' ? 'muted' : 'warn'}>{admin.status}</td>
                    <td className="muted small">
                      {admin.lastLoginAt ? new Date(admin.lastLoginAt).toLocaleString() : 'never'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>
    </>
  );
}

function CreateAdmin(): JSX.Element {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [usernameTouched, setUsernameTouched] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      request<{ id: string; email: string; outcome: string }>('POST', '/admin/admins', {
        email,
        username,
        password,
      }),
    onSuccess: (result) => {
      setCreated(result.email);
      setError(null);
      setEmail('');
      setUsername('');
      setUsernameTouched(false);
      setPassword('');
      void queryClient.invalidateQueries({ queryKey: ['admin', 'admins'] });
    },
    onError: (caught) => {
      setCreated(null);
      setError(errorMessage(caught));
    },
  });

  const tooShort = password.length > 0 && password.length < 12;
  const ready = email.trim() && username.trim().length >= 3 && password.length >= 12;

  return (
    <section className="card">
      <strong>Add an administrator</strong>
      <p className="muted small" style={{ marginTop: 4 }}>
        Created in the <code>instance</code> tenant, not inside a client. The email must not
        already exist under any client — sign-in resolves accounts by email, and this console has
        no client field, so a shared address could never sign in here.
      </p>

      <div className="grid2" style={{ marginTop: 10 }}>
        <div className="field">
          <label>Email</label>
          <input
            type="email"
            value={email}
            placeholder="ops@example.com"
            onChange={(e) => {
              setEmail(e.target.value);
              if (!usernameTouched) setUsername(deriveUsername(e.target.value));
            }}
          />
        </div>
        <div className="field">
          <label>Username</label>
          <input
            value={username}
            placeholder="ops"
            onChange={(e) => {
              setUsername(e.target.value);
              setUsernameTouched(true);
            }}
          />
        </div>
        <div className="field">
          <label>Password (at least 12 characters)</label>
          {/* Visible, not masked: it has to be handed to the person anyway. */}
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              value={password}
              placeholder="Set or generate"
              autoComplete="off"
              onChange={(e) => setPassword(e.target.value)}
            />
            <button type="button" className="sm" onClick={() => setPassword(generatePassword())}>
              Generate
            </button>
          </div>
          {tooShort && <span className="warn small">Use at least 12 characters.</span>}
        </div>
      </div>

      {error && <p className="error small">{error}</p>}
      {created && !error && (
        <p className="ok small">
          {created} can now sign in to this console with the password you set.
        </p>
      )}

      <button className="primary" onClick={() => create.mutate()} disabled={!ready || create.isPending}>
        {create.isPending ? 'Creating…' : 'Add administrator'}
      </button>

      <p className="muted small" style={{ marginBottom: 0, marginTop: 12 }}>
        No administrator yet on a fresh instance? Run{' '}
        <code>pnpm admin:create -- --email you@example.com --username you</code> against the
        database — it prints a generated password once.
      </p>
    </section>
  );
}

function deriveUsername(email: string): string {
  return (email.split('@')[0] ?? '').replace(/[^a-zA-Z0-9._-]/g, '').slice(0, 50);
}

function generatePassword(): string {
  // No ambiguous characters (0/O, 1/l/I) — this gets read aloud or retyped.
  const alphabet = 'abcdefghjkmnpqrstuvwxyzABCDEFGHJKMNPQRSTUVWXYZ23456789';
  const bytes = new Uint8Array(20);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => alphabet[byte % alphabet.length]).join('');
}
